import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ordersAPI, itemsAPI, maestrosAPI, locationsAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import { 
  Search, Save, Send, Check, ChevronRight, Filter, ArrowLeft, 
  Package, AlertTriangle, TrendingDown, Info, ChevronDown, ChevronUp,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const LocalOrderPage = () => {
  // Estados principales
  const [step, setStep] = useState('select-maestro');
  const [maestrosOptions, setMaestrosOptions] = useState([]);
  const [myLocation, setMyLocation] = useState(null);
  const [maestroOrders, setMaestroOrders] = useState({});
  const [selectedMaestro, setSelectedMaestro] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estado del pedido con stock declarado
  const [orderData, setOrderData] = useState({}); // {item_id: {stock: number, quantity: number}}
  const [currentOrder, setCurrentOrder] = useState(null);
  const [previousStockData, setPreviousStockData] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});

  // Cargar maestros
  const loadMaestros = useCallback(async (locationMaestros = null) => {
    try {
      const response = await maestrosAPI.getAll(true);
      let maestros = response.data.map(m => ({
        id: m.name,
        name: m.name,
        description: m.description || '',
        originalId: m.id
      }));
      
      if (locationMaestros && locationMaestros.length > 0) {
        const locationMaestrosLower = locationMaestros.map(m => m.toLowerCase().trim());
        maestros = maestros.filter(m => {
          const nameMatch = locationMaestrosLower.includes(m.name.toLowerCase().trim());
          const idMatch = locationMaestros.includes(m.originalId);
          return nameMatch || idMatch;
        });
      }
      
      setMaestrosOptions(maestros);
      return maestros;
    } catch (error) {
      console.error('Error al cargar maestros:', error);
      return [];
    }
  }, []);

  // Cargar datos iniciales
  const loadInitialData = useCallback(async () => {
    try {
      const today = getToday();
      
      let locationInfo = null;
      try {
        const locationRes = await locationsAPI.getMyInfo();
        locationInfo = locationRes.data;
        setMyLocation(locationInfo);
      } catch (err) {
        console.log('No se pudo obtener info del local:', err);
      }
      
      const locationMaestros = locationInfo?.maestros || [];
      const [itemsRes, previousStockRes] = await Promise.all([
        itemsAPI.getAll(true),
        ordersAPI.getPreviousDeclaredStock().catch(() => ({ data: null }))
      ]);
      
      setAllItems(itemsRes.data);
      setPreviousStockData(previousStockRes.data);
      await loadMaestros(locationMaestros);

      // Cargar pedidos existentes
      try {
        const ordersRes = await ordersAPI.getCurrentAllMaestros(today);
        const ordersMap = {};
        ordersRes.data.forEach(order => {
          const maestroKey = order.maestro || 'all';
          ordersMap[maestroKey] = order;
        });
        setMaestroOrders(ordersMap);
      } catch (err) {
        setMaestroOrders({});
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [loadMaestros]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Extraer proveedores únicos
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(items.map(item => item.supplier || 'Sin asignar'))];
    return uniqueSuppliers.sort();
  }, [items]);

  // Filtrar items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchQuery || 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.code.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesSupplier = selectedSupplier === 'all' || 
        (item.supplier || 'Sin asignar') === selectedSupplier;
      
      return matchesSearch && matchesSupplier;
    });
  }, [items, searchQuery, selectedSupplier]);

  // Agrupar por proveedor
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const supplier = item.supplier || 'Sin asignar';
      if (!groups[supplier]) groups[supplier] = [];
      groups[supplier].push(item);
    });
    return Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [filteredItems]);

  // Productos con pedido
  const itemsWithOrder = useMemo(() => {
    return Object.entries(orderData).filter(([_, data]) => data.quantity > 0).length;
  }, [orderData]);

  // Total de unidades a pedir
  const totalUnits = useMemo(() => {
    return Object.values(orderData).reduce((sum, data) => sum + (data.quantity || 0), 0);
  }, [orderData]);

  // Alertas de consumo inusual
  const getConsumptionAlert = (itemId, currentStock) => {
    if (!previousStockData?.previous_stock?.[itemId]) return null;
    
    const prevData = previousStockData.previous_stock[itemId];
    const prevStock = prevData.declared_stock;
    const prevOrdered = prevData.requested_quantity;
    
    // Stock esperado = stock anterior + pedido - consumo esperado
    // Si el stock actual es muy diferente, alertar
    const expectedMinStock = 0;
    const consumption = prevStock - currentStock + prevOrdered;
    
    if (currentStock < 0) {
      return { type: 'error', message: 'Stock no puede ser negativo' };
    }
    
    if (consumption < 0) {
      return { type: 'warning', message: `Stock mayor al esperado (tenías ${prevStock}, pediste ${prevOrdered})` };
    }
    
    return null;
  };

  // Alerta si pide más de lo usual
  const getOrderAlert = (itemId, quantity) => {
    if (!previousStockData?.averages_7_days?.[itemId] || quantity <= 0) return null;
    
    const avg = previousStockData.averages_7_days[itemId];
    const threshold = avg.avg_quantity * 2; // 2x el promedio
    
    if (quantity > threshold) {
      return { type: 'warning', message: `Pedido alto (promedio: ${avg.avg_quantity})` };
    }
    
    return null;
  };

  // Seleccionar maestro
  const selectMaestro = (maestro) => {
    setSelectedMaestro(maestro);
    setSelectedSupplier('all');
    setSearchQuery('');
    setIsEditing(false);
    
    // Filtrar items
    let filtered = maestro.id === 'all' 
      ? allItems 
      : allItems.filter(item => item.maestros && item.maestros.includes(maestro.id));
    setItems(filtered);
    
    // Expandir todos los proveedores por defecto
    const expandedMap = {};
    [...new Set(filtered.map(item => item.supplier || 'Sin asignar'))].forEach(s => {
      expandedMap[s] = true;
    });
    setExpandedSuppliers(expandedMap);
    
    // Cargar pedido existente
    const existingOrder = maestroOrders[maestro.id];
    if (existingOrder) {
      setCurrentOrder(existingOrder);
      const dataMap = {};
      existingOrder.items.forEach(item => {
        dataMap[item.item_id] = {
          stock: item.declared_stock ?? 0,
          quantity: item.requested_quantity
        };
      });
      setOrderData(dataMap);
    } else {
      setCurrentOrder(null);
      setOrderData({});
    }
    
    setStep('order');
  };

  // Volver a selección de maestro
  const goBackToMaestroSelection = async () => {
    await loadInitialData();
    setStep('select-maestro');
    setSelectedMaestro(null);
    setSelectedSupplier('all');
    setSearchQuery('');
    setOrderData({});
    setCurrentOrder(null);
    setIsEditing(false);
  };

  // Actualizar stock declarado
  const updateStock = (itemId, value) => {
    const numValue = Math.max(0, parseFloat(value) || 0);
    setOrderData(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], stock: numValue, quantity: prev[itemId]?.quantity || 0 }
    }));
  };

  // Actualizar cantidad a pedir
  const updateQuantity = (itemId, value) => {
    const numValue = Math.max(0, parseFloat(value) || 0);
    setOrderData(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: numValue, stock: prev[itemId]?.stock || 0 }
    }));
  };

  // Incrementar cantidad
  const incrementQuantity = (itemId, amount = 1) => {
    setOrderData(prev => {
      const current = prev[itemId]?.quantity || 0;
      return {
        ...prev,
        [itemId]: { ...prev[itemId], quantity: current + amount, stock: prev[itemId]?.stock || 0 }
      };
    });
  };

  // Guardar borrador
  const saveOrder = async () => {
    const itemsToSave = Object.entries(orderData)
      .filter(([_, data]) => data.quantity > 0)
      .map(([itemId, data]) => ({
        item_id: itemId,
        requested_quantity: data.quantity,
        declared_stock: data.stock
      }));

    if (itemsToSave.length === 0) {
      toast.error('Agrega al menos un producto al pedido');
      return;
    }

    setSaving(true);
    try {
      const response = await ordersAPI.create({
        order_date: getToday(),
        items: itemsToSave,
        maestro: selectedMaestro?.id !== 'all' ? selectedMaestro.id : null
      });
      setCurrentOrder(response.data);
      setMaestroOrders(prev => ({
        ...prev,
        [selectedMaestro.id]: response.data
      }));
      toast.success('Pedido guardado como borrador');
      setIsEditing(false);
    } catch (error) {
      toast.error('Error al guardar pedido');
    } finally {
      setSaving(false);
    }
  };

  // Abrir confirmación antes de enviar
  const openConfirmDialog = () => {
    const itemsToSend = Object.entries(orderData).filter(([_, data]) => data.quantity > 0);
    if (itemsToSend.length === 0) {
      toast.error('Agrega al menos un producto al pedido');
      return;
    }
    setConfirmDialogOpen(true);
  };

  // Enviar pedido
  const submitOrder = async () => {
    setConfirmDialogOpen(false);
    setSaving(true);
    
    try {
      // Primero guardar
      const itemsToSave = Object.entries(orderData)
        .filter(([_, data]) => data.quantity > 0)
        .map(([itemId, data]) => ({
          item_id: itemId,
          requested_quantity: data.quantity,
          declared_stock: data.stock
        }));

      const saveResponse = await ordersAPI.create({
        order_date: getToday(),
        items: itemsToSave,
        maestro: selectedMaestro?.id !== 'all' ? selectedMaestro.id : null
      });

      // Luego enviar
      await ordersAPI.submit(saveResponse.data.id);
      
      toast.success('¡Pedido enviado correctamente!');
      await goBackToMaestroSelection();
    } catch (error) {
      toast.error('Error al enviar pedido');
    } finally {
      setSaving(false);
    }
  };

  // Toggle proveedor expandido
  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier]
    }));
  };

  // Habilitar edición
  const enableEditing = () => {
    setIsEditing(true);
    toast.info('Modo edición activado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // PASO 1: Selección de Maestro
  if (step === 'select-maestro') {
    return (
      <div className="space-y-4 pb-20" data-testid="maestro-selection">
        <div className="sticky top-0 bg-background z-10 pb-4">
          <h1 className="text-2xl font-bold text-slate-900">Pedido del Día</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {myLocation?.name || 'Mi Local'} • {getToday()}
          </p>
        </div>

        <div className="space-y-3">
          {/* Opción: Todos los productos */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => selectMaestro({ id: 'all', name: 'Todos los productos' })}
            className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm hover:border-primary hover:shadow-md transition-all active:scale-[0.98]"
            data-testid="maestro-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Todos los productos</p>
                <p className="text-sm text-muted-foreground">{allItems.length} productos disponibles</p>
              </div>
            </div>
            {maestroOrders['all'] && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                {maestroOrders['all'].status === 'submitted' ? 'Enviado' : 'Borrador'}
              </span>
            )}
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </motion.button>

          {/* Maestros */}
          {maestrosOptions.map((maestro, index) => {
            const order = maestroOrders[maestro.id];
            const itemCount = allItems.filter(item => 
              item.maestros && item.maestros.includes(maestro.id)
            ).length;
            
            return (
              <motion.button
                key={maestro.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => selectMaestro(maestro)}
                className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm hover:border-primary hover:shadow-md transition-all active:scale-[0.98]"
                data-testid={`maestro-${maestro.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                    <span className="text-xl">👨‍🍳</span>
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">{maestro.name}</p>
                    <p className="text-sm text-muted-foreground">{itemCount} productos</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {order && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      order.status === 'submitted' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {order.status === 'submitted' ? 'Enviado' : 'Borrador'}
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // PASO 2: Crear/Editar Pedido
  const isSubmitted = currentOrder?.status === 'submitted';
  const canEdit = !isSubmitted || isEditing;

  return (
    <div className="space-y-4 pb-40 md:pb-32" data-testid="order-form">
      {/* Header sticky */}
      <div className="sticky top-0 bg-background z-20 pb-2 -mx-4 px-4 pt-2">
        <div className="flex items-center gap-3 mb-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={goBackToMaestroSelection}
            className="shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">
              {selectedMaestro?.name || 'Pedido'}
            </h1>
            <p className="text-xs text-muted-foreground">{getToday()}</p>
          </div>
          {isSubmitted && !isEditing && (
            <Button variant="outline" size="sm" onClick={enableEditing}>
              Editar
            </Button>
          )}
        </div>

        {/* Búsqueda y filtro */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
              data-testid="search-input"
            />
          </div>
          <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
            <SelectTrigger className="w-[140px] h-10">
              <Filter className="w-4 h-4 mr-1" />
              <SelectValue placeholder="Proveedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {suppliers.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Instrucciones */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-800">
            Indica tu <strong>stock actual</strong> y la <strong>cantidad a pedir</strong> para cada producto.
          </p>
        </div>
      </div>

      {/* Lista de productos por proveedor */}
      <div className="space-y-4">
        {Object.entries(groupedBySupplier).map(([supplier, supplierItems]) => (
          <div key={supplier} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {/* Header del proveedor */}
            <button
              onClick={() => toggleSupplier(supplier)}
              className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-200"
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{supplier}</span>
                <span className="text-xs text-muted-foreground">({supplierItems.length})</span>
              </div>
              {expandedSuppliers[supplier] ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {/* Items del proveedor */}
            <AnimatePresence>
              {expandedSuppliers[supplier] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="divide-y divide-slate-100">
                    {supplierItems.map((item) => {
                      const data = orderData[item.id] || { stock: 0, quantity: 0 };
                      const prevStock = previousStockData?.previous_stock?.[item.id];
                      const stockAlert = getConsumptionAlert(item.id, data.stock);
                      const orderAlert = getOrderAlert(item.id, data.quantity);
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`p-4 ${data.quantity > 0 ? 'bg-green-50/50' : ''}`}
                          data-testid={`product-${item.id}`}
                        >
                          {/* Nombre del producto */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0 pr-2">
                              <p className="font-medium text-slate-900 leading-tight">{item.name}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {item.code} • {item.unit}
                              </p>
                            </div>
                            {data.quantity > 0 && (
                              <Check className="w-5 h-5 text-green-600 shrink-0" />
                            )}
                          </div>

                          {/* Referencia del día anterior */}
                          {prevStock && (
                            <div className="mb-3 p-2 bg-slate-50 rounded-lg text-xs">
                              <span className="text-slate-500">Ayer: </span>
                              <span className="text-slate-700">
                                Stock {prevStock.declared_stock} • Pedido {prevStock.requested_quantity}
                              </span>
                            </div>
                          )}

                          {/* Inputs de stock y cantidad */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Stock actual */}
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                Stock Actual
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                placeholder="0"
                                value={data.stock || ''}
                                onChange={(e) => updateStock(item.id, e.target.value)}
                                disabled={!canEdit}
                                className="h-12 text-center text-lg font-semibold"
                                data-testid={`stock-${item.id}`}
                              />
                              {stockAlert && (
                                <p className={`text-xs mt-1 ${stockAlert.type === 'error' ? 'text-red-600' : 'text-amber-600'}`}>
                                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                                  {stockAlert.message}
                                </p>
                              )}
                            </div>

                            {/* Cantidad a pedir */}
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                A Pedir
                              </label>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-12 w-10 shrink-0"
                                  onClick={() => updateQuantity(item.id, Math.max(0, (data.quantity || 0) - 1))}
                                  disabled={!canEdit || (data.quantity || 0) <= 0}
                                >
                                  <span className="text-xl">-</span>
                                </Button>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  placeholder="0"
                                  value={data.quantity || ''}
                                  onChange={(e) => updateQuantity(item.id, e.target.value)}
                                  disabled={!canEdit}
                                  className="h-12 text-center text-lg font-semibold flex-1"
                                  data-testid={`quantity-${item.id}`}
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-12 w-10 shrink-0"
                                  onClick={() => incrementQuantity(item.id)}
                                  disabled={!canEdit}
                                >
                                  <span className="text-xl">+</span>
                                </Button>
                              </div>
                              {orderAlert && (
                                <p className="text-xs mt-1 text-amber-600">
                                  <TrendingDown className="w-3 h-3 inline mr-1" />
                                  {orderAlert.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* Footer fijo con resumen y botones */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg z-[60]">
        <div className="max-w-lg mx-auto">
          {/* Resumen */}
          <div className="flex items-center justify-between mb-3 text-sm">
            <span className="text-slate-600">
              <strong className="text-slate-900">{itemsWithOrder}</strong> productos
            </span>
            <span className="text-slate-600">
              Total: <strong className="text-primary text-lg">{totalUnits}</strong> unidades
            </span>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={saveOrder}
                  disabled={saving || itemsWithOrder === 0}
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
                <Button
                  className="flex-1"
                  onClick={openConfirmDialog}
                  disabled={saving || itemsWithOrder === 0}
                  data-testid="submit-order-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Pedido
                </Button>
              </>
            )}
            {isSubmitted && !isEditing && (
              <div className="w-full text-center py-2 bg-green-50 text-green-700 rounded-lg">
                <Check className="w-5 h-5 inline mr-2" />
                Pedido enviado
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Confirmar Envío de Pedido
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-600">
              Estás a punto de enviar tu pedido. Una vez enviado, la bodega comenzará a prepararlo.
            </p>
            
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between mb-2">
                <span className="text-slate-600">Productos:</span>
                <span className="font-semibold">{itemsWithOrder}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Total unidades:</span>
                <span className="font-bold text-primary text-lg">{totalUnits}</span>
              </div>
            </div>

            {/* Resumen de productos con alertas */}
            {Object.entries(orderData).some(([id, data]) => 
              data.quantity > 0 && getOrderAlert(id, data.quantity)
            ) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Algunos productos tienen cantidades mayores al promedio
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submitOrder} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirmar y Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalOrderPage;
